// src/utils/carteleria/ingresosHardwareERMotor.js
//
// F3d (rediseño de Cartelería, "lo histórico no se toca"): ingresos de HARDWARE de `negocios_carteleria` del ER v2
// (`useHardwareCalcs.calcIngresosHardware`) para los meses >= CORTE_HISTORICO (2026-10), leyendo del motor
// `calendarioNegocio`. Los meses anteriores salen de `historico/ingresosHardwareERHistorico.js` (copia literal).
//
// Criterio del ER que NO cambia (D11): DEVENGADO REAL (10.143) — con documento (tramo con `facturafecha`, otros
// con `fecha_factura`, OPEX facturado real) el mes es el del documento; sin documento, CAPEX en el mes de
// `fecha_cierre` (TC de fin de mes) y OPEX mes a mes. Lo que se lee del motor: monto/tramos/otros CAPEX (D12:
// tramo ÚNICO sin monto vale `monto_hardware`) y la ventana/cuota OPEX (fin de contrato; "el estado manda").
// La conversión UF/USD → local es la del hook (mismo TC, mismo `Math.round`), envuelta en el helper único.
import { parseLocalYMD } from "../fechasFuturosPanel";
import { calendarioNegocio } from "./calendarioNegocio";
import { convertirMontoCarteleria } from "./convertirMontoCarteleria";
import { CORTE_HISTORICO } from "./corteHistorico";
import { calcIngresosHardwareHistorico } from "./historico/ingresosHardwareERHistorico";

const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);

// Ventana OPEX del código anterior = `fecha_cierre` + (num_cuotas || plazo || 36) meses. El motor la calcula desde
// `fecha_inicio` + plazo (con "el estado manda"): solo se lee del motor cuando ambas arrancan en el mismo mes y
// cuentan las mismas cuotas; si no, ese negocio sigue con la lógica histórica también desde 2026-10.
export const ventanaOpexLeibleDelMotor = (n, cal) => {
  const plazo = parseInt(n.plazo_contrato_meses, 10) || 0;
  const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;
  return (
    plazo > 0 &&
    totalCuotas === plazo &&
    !!n.fecha_inicio &&
    String(n.fecha_cierre).slice(0, 7) === String(n.fecha_inicio).slice(0, 7) &&
    cal.devengo.some((d) => d.componente === "opex")
  );
};

export const calcIngresosHardwareMotor = (negocios, year, paisCalc, getDolarFn, costosGestion, getUFValue, hoy) => {
  const defaultMoneda = paisCalc === "Chile" ? "UF" : "PEN";
  const mapa = Array(12).fill(0);
  negocios.forEach((n) => {
    if (!n.incluye_hardware || n.eliminada) return;
    if (!["100%", "Activo"].includes(n.estado)) return;
    const moneda = (n.moneda_hardware || defaultMoneda).toUpperCase();
    // Misma conversión de siempre (UF solo en Chile, USD con el dólar del país), por el helper.
    const convertir = (monto, fecha) =>
      convertirMontoCarteleria(
        (m) => {
          if (moneda === "UF" && paisCalc === "Chile") return Math.round(m * getUFValue(fecha));
          if (moneda === "US$" || moneda === "USD") return Math.round(m * getDolarFn(fecha));
          return m;
        },
        monto,
        moneda,
        "LOCAL"
      );
    const cal = calendarioNegocio(n, { hoy });
    const capex = cal.hardwareCapex;
    let yaProcesadoConDesglose = false;

    // CAPEX con desglose (devengado real: solo tramos con fecha real de factura). D12: un tramo ÚNICO sin monto
    // vale `monto_hardware` (monto efectivo del motor) y se reconoce en el mes de su factura.
    const detalle = Array.isArray(n.detalle_pagos_hardware) ? n.detalle_pagos_hardware : [];
    const hayDesgloseAntes = detalle.some((p) => p.monto != null);
    // D12 se aplica SOLO si el tramo (con factura real) cae desde el corte y no duplica ni pierde lo que el código
    // anterior ya ubicó en `fecha_cierre` (si el cierre es anterior al corte, ese monto ya está en los meses
    // históricos; D12 no lo reubica).
    const d12Aplica =
      n.modalidad === "CAPEX" &&
      detalle.length === 1 &&
      !(Number(detalle[0].monto) > 0) &&
      !!(detalle[0].facturafecha || detalle[0].fecha_factura) &&
      capex.tramos[0].montoEfectivo > 0 &&
      String(detalle[0].facturafecha || detalle[0].fecha_factura).slice(0, 7) >= CORTE_HISTORICO &&
      (hayDesgloseAntes || !n.fecha_cierre || String(n.fecha_cierre).slice(0, 7) >= CORTE_HISTORICO);
    if (n.modalidad === "CAPEX" && Array.isArray(n.detalle_pagos_hardware) && (hayDesgloseAntes || d12Aplica)) {
      detalle.forEach((pago, i) => {
        const fechaTramo = pago.facturafecha || pago.fecha_factura;
        if (!fechaTramo || (pago.monto == null && !d12Aplica)) return;
        const monto = d12Aplica ? capex.tramos[i].montoEfectivo : capex.tramos[i].monto;
        const fecha = fechaLocalDeNegocio(fechaTramo);
        if (fecha.getFullYear() !== year) return;
        const fechaUsada = fechaTramo.split("T")[0];
        mapa[fecha.getMonth()] += convertir(monto, fechaUsada);
      });
      yaProcesadoConDesglose = true;
    }

    // OTROS CAPEX
    if (n.modalidad === "CAPEX" && Array.isArray(n.detalle_otros_hardware) && n.detalle_otros_hardware.length > 0) {
      n.detalle_otros_hardware.forEach((pago, i) => {
        if (!pago.fecha_factura || pago.monto == null) return;
        const fecha = fechaLocalDeNegocio(pago.fecha_factura);
        if (fecha.getFullYear() !== year) return;
        mapa[fecha.getMonth()] += convertir(capex.otros[i].monto, pago.fecha_factura.split("T")[0]);
      });
    }

    // CAPEX sin desglose
    if (n.modalidad === "CAPEX" && n.fecha_cierre && !yaProcesadoConDesglose) {
      const fechaCierre = fechaLocalDeNegocio(n.fecha_cierre);
      const mesCierre = fechaCierre.getMonth();
      if (fechaCierre.getFullYear() !== year) return;
      const fechaStr = new Date(year, mesCierre + 1, 0).toISOString().split("T")[0];
      mapa[mesCierre] += convertir(capex.monto, fechaStr);
    }
    // OPEX
    else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
      // Modo "facturado real": meses_facturacion con entries OPEX → el mes del documento (D11, sin cambio).
      const mesesFact = Array.isArray(n.meses_facturacion) ? n.meses_facturacion : [];
      const opexEntries = mesesFact.filter((e) => e && e.tipo === "opex" && e.facturafecha);
      if (opexEntries.length > 0) {
        const cuota = (Number(n.cuota_hardware_opex) || 0) * (Number(n.cantidad_pantallas) || 1);
        opexEntries.forEach((e) => {
          const [fy, fm] = e.facturafecha.split("-").map(Number);
          if (fy !== year) return;
          const idx = fm - 1;
          if (idx < 0 || idx > 11) return;
          mapa[idx] += convertir(cuota, e.facturafecha);
        });
        return;
      }
      // Sin documento: mes a mes con ventana y cuota del motor (fin de contrato; el estado manda).
      if (!ventanaOpexLeibleDelMotor(n, cal)) {
        calcIngresosHardwareHistorico([n], year, paisCalc, getDolarFn, costosGestion, getUFValue).forEach((v, m) => {
          mapa[m] += v;
        });
        return;
      }
      cal.devengo
        .filter((d) => d.componente === "opex" && d.ano === year)
        .forEach((d) => {
          const mesActual = d.mesNum - 1;
          const registro = (costosGestion || []).find(
            (c) =>
              Number(c.negocio_id) === n.id &&
              Number(c.mes) === mesActual + 1 &&
              Number(c.ano) === year &&
              c.fecha_factura
          );
          const ultimoDia = new Date(year, mesActual + 1, 0);
          const fechaFactura = registro?.fecha_factura?.split("T")[0] || ultimoDia.toISOString().split("T")[0];
          mapa[mesActual] += convertir(d.monto, fechaFactura);
        });
    }
  });
  return mapa;
};
